import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Megaphone, AtSign, Ticket as TicketIcon, CheckSquare, Check } from 'lucide-react';
import { listCrmInbox, markInboxRead, markAllInboxRead } from '../services/api';

const KIND_META = {
  broadcast: { icon: Megaphone, label: 'Broadcast', route: '/admin/broadcasts', color: '#0f5c3d' },
  mention: { icon: AtSign, label: 'Mention', route: '/admin/crm', color: '#c26a2f' },
  ticket_assigned: { icon: TicketIcon, label: 'Ticket', route: '/admin/crm', color: '#0b1a2e' },
  task_assigned: { icon: CheckSquare, label: 'Task', route: '/admin', color: '#0f5c3d' },
  approval_pending: { icon: CheckSquare, label: 'Approval', route: '/admin/approvals-inbox', color: '#0f5c3d' },
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

const NotificationBell = ({ variant = 'desktop' }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const res = await listCrmInbox();
      setItems(res.data || []);
      setUnread(res.unread || 0);
    } catch (e) {
      // silent — no auth or transient
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 45000); // poll every 45s
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const jump = async (item) => {
    if (!item.read) {
      try { await markInboxRead(item.id); } catch (_) { /* ignore */ }
      setUnread((u) => Math.max(0, u - 1));
      setItems((arr) => arr.map((x) => (x.id === item.id ? { ...x, read: true } : x)));
    }
    setOpen(false);
    const meta = KIND_META[item.kind] || KIND_META.broadcast;
    navigate(meta.route);
  };

  const markAll = async () => {
    if (unread === 0) return;
    try { await markAllInboxRead(); } catch (_) { /* ignore */ }
    setUnread(0);
    setItems((arr) => arr.map((x) => ({ ...x, read: true })));
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`notification-bell-button-${variant}`}
        className="relative flex items-center justify-center w-9 h-9 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span
            data-testid={`notification-bell-badge-${variant}`}
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#c26a2f] text-white text-[10px] font-bold flex items-center justify-center border-2 border-[#0b1a2e]"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-bell-panel"
          className="absolute right-0 mt-2 w-[340px] max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl border border-black/5 overflow-hidden z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-[#f7fbf8]">
            <div>
              <div className="text-sm font-semibold text-[#0b1a2e]">Notifications</div>
              <div className="text-[11px] text-[#0b1a2e]/60">
                {unread > 0 ? `${unread} unread` : 'All caught up'}
              </div>
            </div>
            {unread > 0 && (
              <button
                onClick={markAll}
                data-testid="notification-mark-all-read"
                className="flex items-center gap-1 text-xs text-[#0f5c3d] hover:text-[#0b1a2e] font-medium"
              >
                <Check className="w-3.5 h-3.5" /> Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-[#0b1a2e]/50">
                Nothing new right now.
              </div>
            ) : (
              items.map((item) => {
                const meta = KIND_META[item.kind] || KIND_META.broadcast;
                const Icon = meta.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => jump(item)}
                    data-testid={`notification-item-${item.id}`}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 border-b border-slate-50 hover:bg-[#f7fbf8] transition ${
                      item.read ? 'opacity-70' : 'bg-white'
                    }`}
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: `${meta.color}15`, color: meta.color }}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className="text-[10px] uppercase tracking-widest font-semibold"
                          style={{ color: meta.color }}
                        >
                          {meta.label}
                        </span>
                        {!item.read && (
                          <span className="w-1.5 h-1.5 rounded-full bg-[#c26a2f]" />
                        )}
                        <span className="ml-auto text-[10px] text-[#0b1a2e]/50">
                          {timeAgo(item.created_at)}
                        </span>
                      </div>
                      <div className="text-sm font-medium text-[#0b1a2e] truncate mt-0.5">
                        {item.subject}
                      </div>
                      {item.snippet && (
                        <div className="text-xs text-[#0b1a2e]/60 truncate mt-0.5">
                          {item.snippet}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
